var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useContext, useRef, } from 'react';
import { KeyPressListener } from '../KeyPressListener';
import { Popover } from '../Popover';
import { Stack } from '../Stack';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { Link } from '../Link';
import { DateRangeInput, DateRangeTrigger, DateRangeOptionList, } from './components';
import { MultiViewCalendar, CalendarCell, } from '@progress/kendo-react-dateinputs';
import { Keys } from '../../utilities/keyCodes';
import { isValid } from '../../utilities/dateFormat';
import { TimezoneContext } from '../../utilities/TimezoneProvider';
import { DateTime } from 'luxon';
import { checkScope } from './utils';
import { FormFieldContext } from '../../components/FormField';
export const DateRangePicker = (_a) => {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k;
    var { disabled, error, required, open, disableCloseOnClickOutside, onClickOutside, onOpenChange, focusTrapOptions, dateFormat, value, defaultValue, onChange, maxDate, minDate, isoFormat, input, dropdown, portalDataAttributes, 
    // deprecated props
    trigger, size: dsize = 'medium', placeholder: dplaceholder = 'Select Date Range', direction: ddirection = 'br', autoFlipHorizontally: dautoFlipHorizontally, autoFlipVertically: dautoFlipVertically, portal: dPortal = true, displayValue: ddisplayValue, views: dviews = 1, focusedDate: dfocusedDate, options: doptions, startPlaceholder = 'Start Date', endPlaceholder = 'End Date' } = _a, rest = __rest(_a, ["disabled", "error", "required", "open", "disableCloseOnClickOutside", "onClickOutside", "onOpenChange", "focusTrapOptions", "dateFormat", "value", "defaultValue", "onChange", "maxDate", "minDate", "isoFormat", "input", "dropdown", "portalDataAttributes", "trigger", "size", "placeholder", "direction", "autoFlipHorizontally", "autoFlipVertically", "portal", "displayValue", "views", "focusedDate", "options", "startPlaceholder", "endPlaceholder"]);
    const { portal = dPortal, direction = ddirection, autoFlipVertically = dautoFlipVertically, autoFlipHorizontally = dautoFlipHorizontally, options = doptions, startDateInput, endDateInput, views = dviews, calendar, clearButton, } = Object.assign({}, dropdown);
    const { onChange: CalendarOnChange, focusedDate = dfocusedDate } = Object.assign({}, calendar);
    const { onClick: ClearButtonOnClick } = Object.assign({}, clearButton);
    const { size: inputSize = dsize, placeholder: inputPlaceholder = dplaceholder, displayValue: inputDisplayValue = ddisplayValue, element: inputEl = trigger, } = Object.assign({}, input);
    const { placeholder: startDateInputplaceholder = startPlaceholder, onBlur: startDateInputOnBlur, onChange: startDateInputOnChange, } = Object.assign({}, startDateInput);
    const { placeholder: endDateInputplaceholder = endPlaceholder, onBlur: endDateInputOnBlur, onChange: endDateInputOnChange, } = Object.assign({}, endDateInput);
    const triggerRef = React.useRef(null);
    const timezone = useContext(TimezoneContext);
    const getNewToday = DateTime.fromJSDate(new Date(), {
        zone: timezone,
    });
    const [openCal, setOpenCal] = React.useState(open);
    const [internalError, setInternalError] = React.useState(error);
    const [initialValue, setInitialValue] = React.useState({
        start: (value === null || value === void 0 ? void 0 : value.start)
            ? DateTime.fromJSDate(value.start).setZone(timezone)
            : null,
        end: (value === null || value === void 0 ? void 0 : value.end)
            ? DateTime.fromJSDate(value.end).setZone(timezone)
            : null,
    });
    const [iValue, setIValue] = React.useState(initialValue);
    const minMaxRange = React.useMemo(() => {
        return {
            min: minDate &&
                DateTime.fromJSDate(minDate).startOf('day').setZone(timezone),
            max: maxDate &&
                DateTime.fromJSDate(maxDate).endOf('day').setZone(timezone),
        };
    }, [minDate, timezone, maxDate]);
    const [outOfRange, setOutOfRange] = React.useState(checkScope(value, (_b = minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.min) === null || _b === void 0 ? void 0 : _b.toJSDate(), (_c = minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.max) === null || _c === void 0 ? void 0 : _c.toJSDate()));
    React.useEffect(() => {
        var _a, _b;
        const newValue = {
            start: (value === null || value === void 0 ? void 0 : value.start)
                ? DateTime.fromJSDate(value.start).setZone(timezone)
                : null,
            end: (value === null || value === void 0 ? void 0 : value.end)
                ? DateTime.fromJSDate(value.end).setZone(timezone)
                : null,
        };
        setInitialValue(newValue);
        setIValue(newValue);
        setOutOfRange(checkScope(value, (_a = minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.min) === null || _a === void 0 ? void 0 : _a.toJSDate(), (_b = minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.max) === null || _b === void 0 ? void 0 : _b.toJSDate()));
    }, [minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.max, minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.min, timezone, value]);
    const stopRender = useRef(false);
    React.useEffect(() => {
        let start;
        let end;
        if (isoFormat) {
            start = iValue.start.toISO();
            end = iValue.end.toISO();
        }
        else {
            start = iValue.start ? iValue.start.toJSDate() : null;
            end = iValue.end ? iValue.end.toJSDate() : null;
        }
        stopRender.current && onChange({ start, end });
        stopRender.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timezone]);
    /*
     * handler for MultiViewCalendar onChange
     */
    const blurBlocker = useRef(false);
    const handleCalendarOnChange = (event) => {
        let eventValue = event.target.value;
        blurBlocker.current = true;
        // Force today to select today of the timezone
        if (event.nativeEvent.target.className === 'k-nav-today') {
            event.syntheticEvent.stopPropagation();
            const today = DateTime.now().setZone(timezone);
            if (iValue.start && iValue.end) {
                // reset selection if both values are already filled
                eventValue = {
                    start: today.startOf('day'),
                };
            }
            else {
                if (event.target.activeRangeEnd === 'start') {
                    eventValue = {
                        start: iValue.start ? iValue.start : undefined,
                        end: today.endOf('day'),
                    };
                }
                if (event.target.activeRangeEnd === 'end') {
                    eventValue = {
                        start: today.startOf('day'),
                        end: iValue.end ? iValue.end : undefined,
                    };
                }
                if (eventValue.start > eventValue.end) {
                    const reverseEventValue = {
                        start: eventValue.end,
                        end: eventValue.start,
                    };
                    eventValue = reverseEventValue;
                }
            }
            setIValue(eventValue);
            return;
        }
        const startValue = (eventValue === null || eventValue === void 0 ? void 0 : eventValue.start)
            ? DateTime.fromJSDate(eventValue.start).setZone(timezone, {
                keepLocalTime: true,
            })
            : null;
        const endValue = (eventValue === null || eventValue === void 0 ? void 0 : eventValue.end)
            ? DateTime.fromJSDate(eventValue.end).setZone(timezone, {
                keepLocalTime: true,
            })
            : null;
        setIValue({
            start: startValue,
            end: endValue,
        });
        CalendarOnChange === null || CalendarOnChange === void 0 ? void 0 : CalendarOnChange(event);
    };
    // handler for DateInput onBlur
    const handleDateOnBlur = (e, data, type) => {
        type === 'start'
            ? startDateInputOnBlur === null || startDateInputOnBlur === void 0 ? void 0 : startDateInputOnBlur(e, data)
            : endDateInputOnBlur === null || endDateInputOnBlur === void 0 ? void 0 : endDateInputOnBlur(e, data);
        if (!blurBlocker.current) {
            if (!isValid(data)) {
                setOutOfRange(Object.assign(Object.assign({}, outOfRange), { [type]: 'Enter valid date' }));
                return;
            }
            const value = Object.assign(Object.assign({}, iValue), { [type]: DateTime.fromJSDate(data).setZone(timezone, {
                    keepLocalTime: true,
                }) });
            setIValue(value);
        }
    };
    const handleDateOnChange = (e, data, type) => {
        type === 'start'
            ? startDateInputOnChange === null || startDateInputOnChange === void 0 ? void 0 : startDateInputOnChange(e, data)
            : endDateInputOnChange === null || endDateInputOnChange === void 0 ? void 0 : endDateInputOnChange(e, data);
        setOutOfRange(Object.assign(Object.assign({}, outOfRange), { [type]: false }));
    };
    const handleOptionListOnChange = (data) => {
        const newValue = {
            start: data.start ? DateTime.fromJSDate(data.start) : null,
            end: data.end ? DateTime.fromJSDate(data.end) : null,
        };
        setInitialValue(newValue);
        setIValue(newValue);
        onChange(data);
        setOpenCal(false);
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(false);
    };
    React.useEffect(() => {
        var _a, _b, _c, _d;
        setOutOfRange(checkScope({
            start: (_a = iValue === null || iValue === void 0 ? void 0 : iValue.start) === null || _a === void 0 ? void 0 : _a.toJSDate(),
            end: (_b = iValue === null || iValue === void 0 ? void 0 : iValue.end) === null || _b === void 0 ? void 0 : _b.toJSDate(),
        }, (_c = minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.min) === null || _c === void 0 ? void 0 : _c.toJSDate(), (_d = minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.max) === null || _d === void 0 ? void 0 : _d.toJSDate()));
        blurBlocker.current = false;
    }, [iValue, minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.max, minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.min]);
    React.useEffect(() => {
        var _a, _b, _c, _d;
        const scope = checkScope({
            start: (_a = initialValue === null || initialValue === void 0 ? void 0 : initialValue.start) === null || _a === void 0 ? void 0 : _a.toJSDate(),
            end: (_b = initialValue === null || initialValue === void 0 ? void 0 : initialValue.end) === null || _b === void 0 ? void 0 : _b.toJSDate(),
        }, (_c = minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.min) === null || _c === void 0 ? void 0 : _c.toJSDate(), (_d = minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.max) === null || _d === void 0 ? void 0 : _d.toJSDate());
        if (!scope.start && !scope.end)
            setInternalError(error !== null && error !== void 0 ? error : false);
        if (!!scope.start || !!scope.end)
            setInternalError(true);
    }, [error, initialValue, minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.max, minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.min]);
    // confirm handler, this returns dateRange value through onChange
    const handleOnConfirm = () => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const scope = checkScope({
            start: (_a = iValue === null || iValue === void 0 ? void 0 : iValue.start) === null || _a === void 0 ? void 0 : _a.toJSDate(),
            end: (_b = iValue === null || iValue === void 0 ? void 0 : iValue.end) === null || _b === void 0 ? void 0 : _b.toJSDate(),
        }, (_c = minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.min) === null || _c === void 0 ? void 0 : _c.toJSDate(), (_d = minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.max) === null || _d === void 0 ? void 0 : _d.toJSDate());
        !scope.start && !scope.end
            ? setInternalError(error !== null && error !== void 0 ? error : false)
            : setInternalError(error !== null && error !== void 0 ? error : true);
        setInitialValue(iValue);
        setOpenCal(false);
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(false);
        if (isoFormat) {
            onChange === null || onChange === void 0 ? void 0 : onChange({
                start: (_e = iValue === null || iValue === void 0 ? void 0 : iValue.start) === null || _e === void 0 ? void 0 : _e.toISO(),
                end: (_f = iValue === null || iValue === void 0 ? void 0 : iValue.end) === null || _f === void 0 ? void 0 : _f.endOf('day').setZone(timezone).toISO(),
            });
            return;
        }
        onChange === null || onChange === void 0 ? void 0 : onChange({
            start: (_g = iValue === null || iValue === void 0 ? void 0 : iValue.start) === null || _g === void 0 ? void 0 : _g.startOf('day').setZone(timezone).toJSDate(),
            end: (_h = iValue === null || iValue === void 0 ? void 0 : iValue.end) === null || _h === void 0 ? void 0 : _h.endOf('day').setZone(timezone).toJSDate(),
        });
    };
    // close popover function - this will revert dateRange to initial value
    const close = () => {
        if (openCal) {
            setIValue(initialValue);
            setOpenCal(false);
            onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(false);
            !inputEl && triggerRef.current.focus();
        }
    };
    const { labelFor: inputId } = React.useContext(FormFieldContext);
    const handleOnClickOutside = (e) => {
        var _a, _b;
        if (openCal) {
            if ((_b = (_a = e === null || e === void 0 ? void 0 : e.target) === null || _a === void 0 ? void 0 : _a.closest) === null || _b === void 0 ? void 0 : _b.call(_a, `[for="${inputId}"]`))
                return;
            onClickOutside === null || onClickOutside === void 0 ? void 0 : onClickOutside(e);
            if (!disableCloseOnClickOutside) {
                setIValue(initialValue);
                setOpenCal(false);
                onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(false);
            }
        }
    };
    // toggle open state
    const toggleOpen = () => {
        if (openCal) {
            close();
        }
        else {
            setOpenCal(!openCal);
            onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(true);
        }
    };
    // keyboard event for end date input to confirm and close
    const calendarKeyPress = (event) => {
        if (event.code === Keys.Space && !isApplyDisabled) {
            handleOnConfirm();
            event.preventDefault();
        }
    };
    const clearDateRange = (d) => {
        var _a;
        close();
        onChange === null || onChange === void 0 ? void 0 : onChange(d);
        setIValue(d);
        setInternalError((_a = error !== null && error !== void 0 ? error : required) !== null && _a !== void 0 ? _a : false);
        setInitialValue(d);
    };
    const clearInner = (e) => {
        const nullDateRange = { start: null, end: null };
        setIValue(nullDateRange);
        ClearButtonOnClick === null || ClearButtonOnClick === void 0 ? void 0 : ClearButtonOnClick(e, nullDateRange);
    };
    // keyboard event for trigger to open popover
    const triggerKeyPress = (event) => {
        if (event.code === Keys.Enter || event.code === Keys.Space) {
            setOpenCal(true);
            onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(true);
        }
    };
    // Handles when open prop changes
    const initialRender = useRef(true);
    React.useEffect(() => {
        setOpenCal(open);
        if (!initialRender.current) {
            onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(open);
        }
        initialRender.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const hasError = outOfRange.start || outOfRange.end;
    const hasAcceptableValues = (iValue.start && iValue.end) ||
        (iValue.start === null && iValue.end === null);
    const requiredCheck = required && (iValue.start === null || iValue.end === null);
    const isApplyDisabled = (requiredCheck ||
        !hasAcceptableValues ||
        hasError ||
        !outOfRange);
    const triggerProps = {
        onClick: toggleOpen,
    };
    // custom cell to override the today highlight to match timezone today
    const customCell = (props) => {
        if (format(props.value) === format(getNewToday)) {
            return React.createElement(CalendarCell, Object.assign({}, props, { isToday: true }));
        }
        return React.createElement(CalendarCell, Object.assign({}, props, { isToday: false }));
    };
    return (React.createElement(Popover, { open: !disabled && openCal, onClickOutside: handleOnClickOutside, trigger: (_d = inputEl === null || inputEl === void 0 ? void 0 : inputEl(initialValue, triggerProps, openCal, internalError)) !== null && _d !== void 0 ? _d : (React.createElement(DateRangeTrigger, Object.assign({ open: openCal, placeholder: inputPlaceholder, onClick: toggleOpen, onKeyDown: triggerKeyPress, onChange: clearDateRange, value: {
                start: ((_e = initialValue.start) === null || _e === void 0 ? void 0 : _e.isValid)
                    ? new Date(format(initialValue.start))
                    : null,
                end: ((_f = initialValue.end) === null || _f === void 0 ? void 0 : _f.isValid)
                    ? new Date(format(initialValue.end))
                    : null,
            }, displayValue: inputDisplayValue, disabled: disabled, error: internalError, size: inputSize, ref: triggerRef, required: required, "data-anvil-component": "DateRangePicker" }, rest))), direction: direction, padding: null, width: "auto", className: "DateRangePicker__popover", portal: portal, focusTrapOptions: focusTrapOptions, autoFlipHorizontally: autoFlipHorizontally, autoFlipVertically: autoFlipVertically, portalDataAttributes: portalDataAttributes },
        React.createElement(KeyPressListener, { code: Keys.Esc, handler: close, disabled: disableCloseOnClickOutside || !openCal }),
        React.createElement("div", { className: "d-f flex-row" },
            options && options.length > 0 && (React.createElement(DateRangeOptionList, { options: options, min: (_g = minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.min) === null || _g === void 0 ? void 0 : _g.toJSDate(), max: (_h = minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.max) === null || _h === void 0 ? void 0 : _h.toJSDate(), onChange: handleOptionListOnChange })),
            React.createElement("div", { className: "DateRangePicker" },
                React.createElement("div", { className: "DateRangePicker__header" },
                    React.createElement("div", { className: "DateRangePicker__inputGroup" },
                        React.createElement(DateRangeInput, { placeholder: startDateInputplaceholder, value: iValue === null || iValue === void 0 ? void 0 : iValue.start, error: outOfRange === null || outOfRange === void 0 ? void 0 : outOfRange.start, dateFormat: dateFormat, onBlur: (e, data) => {
                                handleDateOnBlur(e, data, 'start');
                            }, onChange: (e, data) => {
                                handleDateOnChange(e, data, 'start');
                            } }),
                        React.createElement(Icon, { name: "arrow_forward", className: "DateRangePicker__fromTo" }),
                        React.createElement(DateRangeInput, { placeholder: endDateInputplaceholder, value: iValue === null || iValue === void 0 ? void 0 : iValue.end, error: outOfRange === null || outOfRange === void 0 ? void 0 : outOfRange.end, dateFormat: dateFormat, onBlur: (e, data) => {
                                handleDateOnBlur(e, data, 'end');
                            }, onChange: (e, data) => {
                                handleDateOnChange(e, data, 'end');
                            } }))),
                React.createElement("div", { onKeyPress: calendarKeyPress, className: "DatePicker__calendar" },
                    React.createElement(MultiViewCalendar, { mode: "range", views: views, value: {
                            start: iValue.start
                                ? new Date(format(iValue.start))
                                : null,
                            end: iValue.end
                                ? new Date(format(iValue.end))
                                : null,
                        }, onChange: handleCalendarOnChange, focusedDate: focusedDate
                            ? focusedDate
                            : !((value === null || value === void 0 ? void 0 : value.start) && (value === null || value === void 0 ? void 0 : value.end))
                                ? new Date()
                                : undefined, defaultValue: defaultValue, min: (_j = minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.min) === null || _j === void 0 ? void 0 : _j.toJSDate(), max: (_k = minMaxRange === null || minMaxRange === void 0 ? void 0 : minMaxRange.max) === null || _k === void 0 ? void 0 : _k.toJSDate(), cell: customCell })))),
        React.createElement(Stack, { className: "DateRangePicker__actions", direction: "row-reverse", justifyContent: "space-between" },
            React.createElement(Stack, { direction: "row-reverse", spacing: 2 },
                React.createElement(Button, { disabled: isApplyDisabled, onClick: handleOnConfirm, small: true, color: "primary" }, "Apply"),
                React.createElement(Button, { small: true, fill: "subtle", onClick: close }, "Cancel")),
            React.createElement(Link, { primary: true, onClick: clearInner, disabled: !((iValue === null || iValue === void 0 ? void 0 : iValue.start) || (iValue === null || iValue === void 0 ? void 0 : iValue.end)), className: "fs-2" }, "Clear"))));
};
const format = (date, format = 'LL/dd/yyyy') => {
    if (date instanceof Date)
        return DateTime.fromJSDate(date).toFormat(format);
    if (date instanceof DateTime)
        return date.toFormat(format);
    return date;
};
//# sourceMappingURL=DateRangePicker.js.map